import React, { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import { createRoot } from "react-dom/client";
import realtiIcon from "../../../packages/brand/icon.png";
import { FolderTree, toggleFolderExpansion, type FolderNode } from "./FolderTree";
import "./styles.css";

type Envelope<T> = { status: string; data: T };
type FileEntry = { name: string; size: number; modified: number; created: number };
type PublicConfiguration = { admin_authorize_url: string; maximum_upload_bytes: number };
type Session = { email: string; role: string; expires_at: number };

function csrfToken() {
  const stored = sessionStorage.getItem("realti-files-csrf");
  if (stored) return stored;
  return document.cookie.split(";").map(value => value.trim()).find(value => value.startsWith("realti_files_csrf="))?.split("=")[1] ?? "";
}

async function request<T>(path: string, method = "GET", body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(method === "GET" ? {} : { "X-CSRF-Token": csrfToken() }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({ message: response.statusText }));
  if (!response.ok) throw Object.assign(new Error(payload.message ?? "Request failed"), { status: response.status });
  return payload as T;
}

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let amount = value / 1024;
  let index = 0;
  while (amount >= 1024 && index < units.length - 1) { amount /= 1024; index += 1; }
  return `${amount >= 10 ? amount.toFixed(1) : amount.toFixed(2)} ${units[index]}`;
}

function encodePath(path: string) { return path.split("/").map(encodeURIComponent).join("/"); }
function joinPath(folder: string, name: string) { return folder ? `${folder}/${name}` : name; }

function FileManager({ configuration, session, onLogout }: { configuration: PublicConfiguration; session: Session; onLogout: () => void }) {
  const [tree, setTree] = useState<FolderNode | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(() => new Set());
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [folder, setFolder] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [upload, setUpload] = useState<{ name: string; percent: number } | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [nextTree, nextFiles] = await Promise.all([
        request<FolderNode>("/api/folder-tree"),
        request<{ files: FileEntry[] }>(`/api/files?folder=${encodeURIComponent(folder)}`),
      ]);
      setTree(nextTree); setFiles(nextFiles.files);
    } catch (requestError) { setError(requestError instanceof Error ? requestError.message : "Files could not be loaded"); }
    finally { setLoading(false); }
  }, [folder]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (!folder.includes("/")) return;
    const parts = folder.split("/");
    setExpandedFolders(current => {
      const next = new Set(current);
      for (let index = 1; index < parts.length; index += 1) {
        next.add(parts.slice(0, index).join("/"));
      }
      return next;
    });
  }, [folder]);

  async function mutation(path: string, body: unknown, success: string) {
    setError(""); setNotice("");
    try { await request(path, "POST", body); setNotice(success); await refresh(); }
    catch (requestError) { setError(requestError instanceof Error ? requestError.message : "Operation failed"); }
  }

  async function createFolder() {
    const name = window.prompt(`Create a folder in ${folder || "All files"}:`);
    if (name?.trim()) await mutation("/api/create-folder", { path: folder, name: name.trim() }, `Created ${name.trim()}.`);
  }

  async function renameFolder() {
    if (!folder) return;
    const next = window.prompt("New folder path:", folder);
    if (next?.trim() && next.trim() !== folder) {
      try {
        await request("/api/rename-folder", "POST", { old: folder, new: next.trim() });
        setNotice(`Renamed ${folder}.`); setError(""); setFolder(next.trim());
      } catch (requestError) { setError(requestError instanceof Error ? requestError.message : "Rename failed"); }
    }
  }

  async function removeFolder() {
    if (!folder || window.prompt(`Type DELETE ${folder} to remove this empty folder:`) !== `DELETE ${folder}`) return;
    const parent = folder.includes("/") ? folder.slice(0, folder.lastIndexOf("/")) : "";
    try {
      await request("/api/delete-folder", "POST", { path: folder });
      setNotice(`Deleted ${folder}.`); setError(""); setFolder(parent);
    } catch (requestError) { setError(requestError instanceof Error ? requestError.message : "Delete failed"); }
  }

  async function uploadOne(file: File) {
    if (file.size > configuration.maximum_upload_bytes) throw new Error(`${file.name} exceeds the ${formatBytes(configuration.maximum_upload_bytes)} limit.`);
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const form = new FormData();
      form.append("folder", folder);
      form.append("file", file, file.name);
      xhr.open("POST", "/api/upload");
      xhr.withCredentials = true;
      xhr.setRequestHeader("X-CSRF-Token", csrfToken());
      xhr.upload.onprogress = event => { if (event.lengthComputable) setUpload({ name: file.name, percent: Math.round(event.loaded / event.total * 100) }); };
      xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(JSON.parse(xhr.responseText || "{}").message ?? `Upload failed (${xhr.status})`));
      xhr.onerror = () => reject(new Error("The upload connection was interrupted"));
      xhr.send(form);
    });
  }

  async function uploadFiles(selected: FileList | File[]) {
    setError(""); setNotice("");
    try {
      for (const file of Array.from(selected)) { setUpload({ name: file.name, percent: 0 }); await uploadOne(file); }
      setNotice(`${selected.length} file${selected.length === 1 ? "" : "s"} uploaded.`);
      await refresh();
    } catch (uploadError) { setError(uploadError instanceof Error ? uploadError.message : "Upload failed"); }
    finally { setUpload(null); if (input.current) input.current.value = ""; }
  }

  function drop(event: DragEvent<HTMLDivElement>) { event.preventDefault(); if (event.dataTransfer.files.length) void uploadFiles(event.dataTransfer.files); }
  const crumbs = folder ? folder.split("/") : [];

  return <div className="file-shell">
    <aside>
      <div className="brand"><img className="brand-icon" src={realtiIcon} alt="Realti" /><div><strong>Realti Files</strong><small>Secure storage</small></div></div>
      <nav>{tree && <FolderTree node={tree} current={folder} expanded={expandedFolders} onSelect={setFolder} onToggle={path => setExpandedFolders(current => toggleFolderExpansion(current, path))} root />}</nav>
      <div className="identity"><strong>{session.email}</strong><span>Superuser</span><button onClick={onLogout}>Sign out</button></div>
    </aside>
    <main>
      <header><div><p className="eyebrow">File operations</p><h1>{folder.split("/").at(-1) || "All files"}</h1><div className="breadcrumbs"><button onClick={() => setFolder("")}>All files</button>{crumbs.map((part, index) => <React.Fragment key={`${part}-${index}`}><span>/</span><button onClick={() => setFolder(crumbs.slice(0, index + 1).join("/"))}>{part}</button></React.Fragment>)}</div></div><button className="secondary" onClick={() => void refresh()}>Refresh</button></header>
      <section className="toolbar"><button className="primary" onClick={() => input.current?.click()}>Upload files</button><button onClick={() => void createFolder()}>New folder</button>{folder && <><button onClick={() => void renameFolder()}>Rename folder</button><button className="danger" onClick={() => void removeFolder()}>Delete empty folder</button></>}<input ref={input} hidden type="file" multiple onChange={event => event.target.files && void uploadFiles(event.target.files)} /></section>
      {error && <div className="message error" role="alert">{error}</div>}{notice && <div className="message success">{notice}</div>}
      {upload && <div className="upload-progress"><div><strong>Uploading {upload.name}</strong><span>{upload.percent}%</span></div><progress max="100" value={upload.percent} /></div>}
      <section className="files-panel" onDragOver={event => event.preventDefault()} onDrop={drop}>
        <div className="panel-heading"><div><h2>Files</h2><p>Drop files here to upload. Individual files may be up to {formatBytes(configuration.maximum_upload_bytes)}.</p></div><span>{files.length} item{files.length === 1 ? "" : "s"}</span></div>
        {loading ? <div className="empty">Loading files…</div> : files.length === 0 ? <div className="empty"><span>⇧</span><h3>This folder is empty</h3><p>Drag a file here or choose Upload files.</p></div> : <div className="table-wrap"><table><thead><tr><th>Name</th><th>Size</th><th>Modified</th><th>Actions</th></tr></thead><tbody>{files.map(file => { const path = joinPath(folder, file.name); return <tr key={file.name}><td><span className="file-icon">▤</span><strong>{file.name}</strong></td><td>{formatBytes(file.size)}</td><td>{new Date(file.modified * 1000).toLocaleString()}</td><td><div className="actions"><a href={`/api/files/${encodePath(path)}`}>Download</a><button onClick={() => { const target = window.prompt("Copy to folder (leave empty for All files):", folder); if (target !== null) void mutation("/api/copy-file", { path, target: target.trim() }, `Copied ${file.name}.`); }}>Copy</button><button className="text-danger" onClick={() => { if (window.confirm(`Delete ${file.name}? This cannot be undone.`)) void mutation("/api/delete-file", { path }, `Deleted ${file.name}.`); }}>Delete</button></div></td></tr>; })}</tbody></table></div>}
      </section>
    </main>
  </div>;
}

function App() {
  const [configuration, setConfiguration] = useState<PublicConfiguration | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    void (async () => {
      try {
        const config = await request<Envelope<PublicConfiguration>>("/api/auth/config");
        setConfiguration(config.data);
        const code = new URLSearchParams(window.location.hash.slice(1)).get("code");
        if (code) {
          window.history.replaceState({}, "", `${window.location.pathname}${window.location.search}`);
          const exchanged = await request<Envelope<Session & { csrf_token: string }>>("/api/auth/exchange", "POST", { code });
          sessionStorage.setItem("realti-files-csrf", exchanged.data.csrf_token);
        }
        const current = await request<Envelope<Session>>("/api/auth/session");
        setSession(current.data);
      } catch (requestError) { setError(requestError instanceof Error ? requestError.message : "Authorization failed"); }
      finally { setLoading(false); }
    })();
  }, []);
  async function logout() {
    try { await request("/api/auth/logout", "POST"); } finally { sessionStorage.removeItem("realti-files-csrf"); setSession(null); }
  }
  if (loading) return <main className="gate"><div className="gate-card"><img className="brand-icon gate-brand-icon" src={realtiIcon} alt="Realti" /><h1>Opening Realti Files</h1><p>Verifying your secure session…</p></div></main>;
  if (!configuration || !session) return <main className="gate"><div className="gate-card"><img className="brand-icon gate-brand-icon" src={realtiIcon} alt="Realti" /><p className="eyebrow">Restricted access</p><h1>Realti Files</h1><p>Password and passkey verification through Realti Admin are required.</p>{error && <div className="message error">{error}</div>}{configuration && <a className="login-link" href={configuration.admin_authorize_url}>Continue to secure login</a>}</div></main>;
  return <FileManager configuration={configuration} session={session} onLogout={() => void logout()} />;
}

createRoot(document.getElementById("root")!).render(<React.StrictMode><App /></React.StrictMode>);
